import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { DocumentsService } from '@/features/documents/services/documents-service';
import { Document } from '@/features/documents/types';
import { Search, FileText, Plus, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface DocumentPickerProps {
  selectedDocumentIds: string[];
  onDocumentsChange: (documentIds: string[]) => void;
  trigger?: React.ReactNode;
  maxSelections?: number;
  excludeDocumentIds?: string[];
}

export function DocumentPicker({
  selectedDocumentIds,
  onDocumentsChange,
  trigger,
  maxSelections = 50,
  excludeDocumentIds = [],
}: DocumentPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<string>('all');
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedDocumentIds);

  useEffect(() => {
    setLocalSelectedIds(selectedDocumentIds);
  }, [selectedDocumentIds]);

  useEffect(() => {
    if (isOpen) {
      fetchDocuments();
    }
  }, [isOpen, searchTerm, documentTypeFilter, visibilityFilter]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      
      const filters: any = {};
      
      if (searchTerm) {
        filters.search_term = searchTerm;
      }
      
      if (documentTypeFilter !== 'all') {
        filters.document_type = [documentTypeFilter];
      }
      
      if (visibilityFilter !== 'all') {
        filters.visibility = [visibilityFilter];
      }

      // Fetch documents with pagination (limit to 100 for picker)
      const result = await DocumentsService.getDocumentsPage(1, 100, filters);
      
      // Filter out excluded documents
      const filteredDocuments = result.documents.filter(
        (doc: Document) => !excludeDocumentIds.includes(doc.id)
      );
      
      setDocuments(filteredDocuments);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentToggle = (documentId: string) => {
    const newSelection = localSelectedIds.includes(documentId)
      ? localSelectedIds.filter(id => id !== documentId)
      : [...localSelectedIds, documentId];
    
    // Enforce max selections
    if (newSelection.length <= maxSelections) {
      setLocalSelectedIds(newSelection);
    }
  };

  const handleSaveSelection = () => {
    onDocumentsChange(localSelectedIds);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setLocalSelectedIds(selectedDocumentIds);
    setIsOpen(false);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDocumentTypeFilter('all');
    setVisibilityFilter('all');
  };

  const selectedDocuments = documents.filter(doc => localSelectedIds.includes(doc.id));
  const availableSelections = maxSelections - localSelectedIds.length;

  const defaultTrigger = (
    <Button variant="outline" size="sm">
      <Plus className="mr-2 h-4 w-4" />
      Select Documents ({selectedDocumentIds.length})
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] w-[90vw] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Select Documents for Collection</DialogTitle>
          <DialogDescription className="break-words">
            Choose documents to add to this collection. You can select up to {maxSelections} documents.
            {availableSelections > 0 
              ? ` ${availableSelections} more selections available.`
              : ' Maximum selections reached.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-hidden">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={documentTypeFilter} onValueChange={setDocumentTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Document Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="photo">Photo</SelectItem>
                <SelectItem value="document">Document</SelectItem>
                <SelectItem value="newspaper">Newspaper</SelectItem>
                <SelectItem value="map">Map</SelectItem>
                <SelectItem value="letter">Letter</SelectItem>
              </SelectContent>
            </Select>

            <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={clearFilters} size="sm">
              Clear
            </Button>
          </div>

          {/* Selected Documents Summary */}
          {localSelectedIds.length > 0 && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">
                  Selected Documents ({localSelectedIds.length}/{maxSelections})
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLocalSelectedIds([])}
                  disabled={localSelectedIds.length === 0}
                >
                  Clear All
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedDocuments.slice(0, 5).map(doc => (
                  <Badge key={doc.id} variant="secondary" className="text-xs">
                    {doc.title.length > 20 ? `${doc.title.substring(0, 20)}...` : doc.title}
                    <button
                      onClick={() => handleDocumentToggle(doc.id)}
                      className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {localSelectedIds.length > 5 && (
                  <Badge variant="outline" className="text-xs">
                    +{localSelectedIds.length - 5} more
                  </Badge>
                )}
              </div>
            </div>
          )}

          <Separator />

          {/* Documents List */}
          <ScrollArea className="h-[400px] w-full">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
              </div>
            ) : documents.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <FileText className="h-8 w-8 mb-2" />
                <p className="text-sm">No documents found</p>
                <p className="text-xs">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="space-y-2 pr-4">
                {documents.map((document) => {
                  const isSelected = localSelectedIds.includes(document.id);
                  const canSelect = availableSelections > 0 || isSelected;
                  
                  return (
                    <div
                      key={document.id}
                      className={`flex items-start space-x-3 p-3 rounded-lg border transition-colors ${
                        isSelected
                          ? 'bg-primary/5 border-primary'
                          : canSelect
                          ? 'hover:bg-muted/50 cursor-pointer'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => canSelect && handleDocumentToggle(document.id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => canSelect && handleDocumentToggle(document.id)}
                        disabled={!canSelect}
                        className="mt-1"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-medium truncate">
                            {document.title}
                          </h4>
                          <Badge variant="outline" className="text-xs">
                            {document.document_type}
                          </Badge>
                          {document.visibility === 'private' && (
                            <Badge variant="secondary" className="text-xs">
                              Private
                            </Badge>
                          )}
                        </div>
                        
                        {document.description && (
                          <p className="text-xs text-muted-foreground mb-1 line-clamp-2">
                            {document.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>ID: {document.id}</span>
                          <span>
                            Created {formatDistanceToNow(new Date(document.audit.created_at * 1000), { addSuffix: true })}
                          </span>
                          {document.original_date && (
                            <span>
                              Original: {document.original_date}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4">
            <div className="text-sm text-muted-foreground">
              {documents.length} documents found
              {localSelectedIds.length > 0 && ` • ${localSelectedIds.length} selected`}
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSaveSelection}>
                Save Selection
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
